/**
 * "Verify wallet": opened from a withdrawal that is under review and asks the client to confirm the wallet it
 * is paid to (WITHDRAWAL_REVIEW_PLAN.md §8, design v5). One sheet, five screens:
 *   S2  Where do you keep this wallet? (an exchange or a wallet app, each with its logos)
 *   V1  Send the exact amount from this wallet to our address (copy buttons, QR code, countdown)
 *   V2  Looking for your transfer      V3  Got it, and it matches (confirmations, what we matched)
 *   V4  Wallet verified                V5  The amount was different / time ran out
 * Our address, the amount and the deadline come from Support; the screen follows the transfer live.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { fmt, type WalletType, type WalletVerificationView, type Withdrawal } from '@platform/shared';
import { cx, Spinner } from '../../../components/ui';
import { api } from '../../../lib/api';
import { useVersion } from '../../../stores/account';
import { CopyButton, errMsg, Field, Notice, PillButton, PillLink, QrCode, when } from '../kit';
import { Sheet } from '../deposit/Sheet';
import { EXCHANGE_LOGOS, LogoStack, WALLET_APP_LOGOS } from './walletLogos';

const amountText = (v: Pick<WalletVerificationView, 'amount' | 'coin'>, a = v.amount) => `${fmt(a, v.coin === 'USDT' ? 2 : 8)} ${v.coin}`;
const CHAIN: Record<string, string> = { TRC20: 'Tron (TRC20)', ERC20: 'Ethereum (ERC20)', ETH: 'Ethereum', BTC: 'Bitcoin' };
const NETWORK_WORD: Record<string, string> = { TRC20: 'Tron', ERC20: 'Ethereum', ETH: 'Ethereum', BTC: 'Bitcoin' };
const short = (s: string) => (s.length > 16 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s);

type Screen = 'type' | 'send' | 'looking' | 'found' | 'done' | 'problem' | 'closed';

function screenOf(v: WalletVerificationView, editingType: boolean): Screen {
  if (v.status === 'approved') return 'done';
  if (v.status === 'rejected' || v.status === 'expired') return 'problem';
  if (v.status === 'cancelled') return 'closed';
  if (v.status === 'found') return 'found';
  if (!v.walletType || editingType) return 'type';
  return v.clientSentAt ? 'looking' : 'send';
}

export function VerifyWallet({ w, onClose }: { w: Withdrawal; onClose: () => void }) {
  const [v, setV] = useState<WalletVerificationView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editingType, setEditingType] = useState(false);
  const [resending, setResending] = useState(false);
  const live = useVersion('walletVerification');
  // Live updates come over the account channel; while we wait on the chain or our team, also look every 15 s.
  const [tick, setTick] = useState(0);
  const waiting = !!v && (v.status === 'found' || (v.status === 'waiting' && !!v.clientSentAt));
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(t);
  }, [waiting]);
  useEffect(() => {
    let gone = false;
    api
      .get<WalletVerificationView>(`/api/withdrawals/${w.id}/verification`)
      .then((x) => !gone && setV(x))
      .catch((e) => !gone && setErr(errMsg(e)));
    return () => {
      gone = true;
    };
  }, [w.id, live, tick]);

  if (!v)
    return (
      <Sheet title="Verify wallet" onClose={onClose}>
        <div className="grid place-items-center py-16 text-muted">{err ? <Notice msg={{ ok: false, text: err }} /> : <Spinner />}</div>
      </Sheet>
    );
  const screen = resending ? 'send' : screenOf(v, editingType);
  return (
    <Sheet title="Verify wallet" onClose={onClose}>
      <div className="mt-5 flex flex-col gap-5">
        {screen === 'type' && <TypeStep v={v} onDone={(x) => (setV(x), setEditingType(false))} />}
        {screen === 'send' && <SendStep v={v} onDone={(x) => (setV(x), setResending(false))} onBack={() => setEditingType(true)} />}
        {screen === 'looking' && <Looking v={v} onUpdate={setV} onNotSent={() => setResending(true)} />}
        {screen === 'found' && <Found v={v} />}
        {screen === 'done' && <Done v={v} onClose={onClose} />}
        {screen === 'problem' && <Problem v={v} onUpdate={setV} />}
        {screen === 'closed' && <div className="rounded-[14px] bg-track px-4 py-3 text-[15px]">You no longer need to verify this wallet for {w.number}.</div>}
      </div>
    </Sheet>
  );
}

/** S2: an exchange or a wallet app, two matching cards with their logos. */
function TypeStep({ v, onDone }: { v: WalletVerificationView; onDone: (v: WalletVerificationView) => void }) {
  const [pick, setPick] = useState<WalletType>(v.walletType ?? 'exchange');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      onDone(await api.post<WalletVerificationView>(`/api/withdrawals/addresses/${v.addressId}/verification/type`, { walletType: pick }));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const card = (t: WalletType, head: string, sub: string, logos: ReactNode) => (
    <label
      className={cx(
        'flex cursor-pointer flex-col gap-3 rounded-[18px] border p-4 transition-colors',
        pick === t ? 'border-accent bg-accent-bg ring-1 ring-accent' : 'border-line-strong hover:border-accent',
      )}
    >
      <span className="flex items-start gap-3">
        <input type="radio" name="wallet-type" value={t} checked={pick === t} onChange={() => setPick(t)} className="mt-1 size-4 accent-[var(--accent)]" />
        <span className="flex flex-col">
          <b className="text-[15px]">{head}</b>
          <span className="text-[13px] text-muted">{sub}</span>
        </span>
      </span>
      {logos}
    </label>
  );
  return (
    <>
      <div className="flex flex-col gap-1">
        <b className="text-[17px]">Where do you keep this wallet?</b>
        <span className="num text-[13px] text-muted">
          {v.addressLabel ?? 'Your wallet'} · {CHAIN[v.network]}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {card('exchange', 'On an exchange', 'An account at a crypto exchange', <LogoStack names={EXCHANGE_LOGOS} />)}
        {card('app', 'In a wallet app', 'You hold the keys yourself', <LogoStack names={WALLET_APP_LOGOS} />)}
      </div>
      {err && <Notice msg={{ ok: false, text: err }} onClose={() => setErr(null)} />}
      <PillButton busy={busy} onClick={() => void save()}>
        Continue
      </PillButton>
    </>
  );
}

function useCountdown(until: string) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, new Date(until).getTime() - now);
  const h = Math.floor(left / 3600_000);
  return { left, text: h >= 24 ? `${Math.floor(h / 24)} d ${h % 24} h left` : h >= 1 ? `${h} h ${Math.floor((left % 3600_000) / 60_000)} min left` : `${Math.ceil(left / 60_000)} min left` };
}

const Num = ({ n }: { n: number }) => <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-bg text-[12px] font-semibold text-accent-fg">{n}</span>;

/** V1: what to send, where, by when. */
function SendStep({ v, onDone, onBack }: { v: WalletVerificationView; onDone: (v: WalletVerificationView) => void; onBack: () => void }) {
  const [tx, setTx] = useState(v.clientTxHash ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cd = useCountdown(v.expiresAt);
  const total = new Date(v.expiresAt).getTime() - new Date(v.createdAt).getTime();
  const sent = async () => {
    setBusy(true);
    setErr(null);
    try {
      onDone(await api.post<WalletVerificationView>(`/api/withdrawals/addresses/${v.addressId}/verification/sent`, tx.trim() ? { txHash: tx.trim() } : {}));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="flex flex-col gap-1">
        <b className="text-[18px] leading-snug">
          Send {amountText(v)} from this wallet
        </b>
        <span className="text-[13px] text-muted">
          From <b className="text-fg">{v.addressLabel ?? 'your wallet'}</b> · back in your balance once it's checked
        </span>
      </div>
      <div className="flex gap-3">
        <Num n={1} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[13px] text-muted">Amount, exactly</span>
          <div className="flex items-center justify-between gap-3 rounded-[14px] bg-track px-4 py-3">
            <span className="num text-[22px] font-semibold">{amountText(v)}</span>
            <CopyButton value={v.amount} size="sm" kind="secondary">
              Copy
            </CopyButton>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <Num n={2} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[13px] text-muted">Our address · {CHAIN[v.network]}</span>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <QrCode value={v.receiveAddress} size={120} label={`QR code of our ${CHAIN[v.network]} address`} />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <span className="num rounded-[12px] bg-track px-3 py-2.5 text-[13px] break-all">{v.receiveAddress}</span>
              <CopyButton value={v.receiveAddress} size="sm" kind="secondary">
                Copy address
              </CopyButton>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <Num n={3} />
        <Field label="Transaction ID (optional, speeds things up)" htmlFor="vw-tx" className="min-w-0 flex-1">
          <input id="vw-tx" className="field num !text-[14px]" placeholder="Paste it here" value={tx} onChange={(e) => setTx(e.target.value)} />
        </Field>
      </div>
      <div className="flex items-center gap-3 text-[13px] text-muted">
        <span>Send by {when(v.expiresAt)}</span>
        <span className="h-1 flex-1 overflow-hidden rounded-full bg-pill">
          <span className="block h-full bg-warn" style={{ width: `${total > 0 ? Math.min(100, (cd.left / total) * 100) : 0}%` }} />
        </span>
        <span>{cd.text}</span>
      </div>
      <ul className="m-0 flex flex-col gap-1 rounded-[14px] bg-track py-3 pr-4 pl-8 text-[13px]">
        <li>
          Use the <b>{v.network}</b> network
        </li>
        <li>
          Send the <b>exact</b> amount, so we can match it to you
        </li>
        {v.walletType === 'exchange' && <li>If your exchange charges a fee, add it on top so {amountText(v)} arrives</li>}
      </ul>
      {err && <Notice msg={{ ok: false, text: err }} onClose={() => setErr(null)} />}
      <div className="flex flex-col gap-2">
        <PillButton busy={busy} onClick={() => void sent()}>
          I've sent it
        </PillButton>
        <button type="button" className="text-[13px] text-muted underline-offset-2 hover:underline" onClick={onBack}>
          Change where I keep this wallet
        </button>
      </div>
    </>
  );
}

/** The progress ring: `part` of 1, with a label inside. */
function Ring({ part, done, children }: { part: number; done?: boolean; children: ReactNode }) {
  const c = 2 * Math.PI * 40;
  return (
    <span className="relative block size-[92px] shrink-0">
      <svg viewBox="0 0 92 92" className="size-full -rotate-90" aria-hidden="true">
        <circle cx="46" cy="46" r="40" fill="none" strokeWidth="8" className="stroke-pill" />
        <circle
          cx="46"
          cy="46"
          r="40"
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className={cx(done ? 'stroke-up' : 'stroke-accent', !done && 'motion-safe:animate-pulse')}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0.04, Math.min(1, part)))}
        />
      </svg>
      <span className="num absolute inset-0 grid place-items-center text-center text-[15px] font-semibold leading-tight">{children}</span>
    </span>
  );
}

type Step = { title: string; sub: ReactNode; state: 'done' | 'now' | 'todo' };
function Tracker({ steps }: { steps: Step[] }) {
  return (
    <ol className="m-0 flex list-none flex-col p-0" aria-label="Progress">
      {steps.map((s, i) => (
        <li key={s.title} aria-current={s.state === 'now' ? 'step' : undefined} className="relative grid grid-cols-[22px_minmax(0,1fr)] gap-3 pb-3 last:pb-0">
          {i < steps.length - 1 && <span className={cx('absolute top-[22px] bottom-0 left-[10px] w-0.5', s.state === 'done' ? 'bg-up' : 'bg-line-strong')} />}
          <span
            className={cx(
              'relative z-[1] grid size-[22px] place-items-center rounded-full border-2 text-[11px] font-bold',
              s.state === 'done' ? 'border-up bg-up text-[var(--btn-long-text)]' : s.state === 'now' ? 'border-accent bg-card' : 'border-line-strong bg-card',
            )}
          >
            {s.state === 'done' ? '✓' : s.state === 'now' ? <span className="size-2 rounded-full bg-accent" /> : null}
          </span>
          <span className="flex min-w-0 flex-col">
            <b className="text-[14px]">{s.title}</b>
            <span className="text-[13px] text-muted">{s.sub}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** V2: looking for the transfer. */
function Looking({ v, onUpdate, onNotSent }: { v: WalletVerificationView; onUpdate: (v: WalletVerificationView) => void; onNotSent: () => void }) {
  const [tx, setTx] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const paste = async () => {
    setBusy(true);
    setMsg(null);
    try {
      onUpdate(await api.post<WalletVerificationView>(`/api/withdrawals/addresses/${v.addressId}/verification/sent`, { txHash: tx.trim() }));
      setMsg({ ok: true, text: 'Thanks. We will look for that transaction.' });
      setTx('');
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="flex items-center gap-4">
        <Ring part={0.3}>
          2<small className="block text-[10px] font-medium text-muted">of 4</small>
        </Ring>
        <div className="flex flex-col gap-1">
          <b className="text-[17px]">Looking for your transfer</b>
          <span className="text-[13px] text-muted">Most arrive within 5 minutes. You can close this window, and we'll keep checking and let you know.</span>
        </div>
      </div>
      <Tracker
        steps={[
          { title: 'You sent it', sub: v.clientSentAt ? when(v.clientSentAt) : '', state: 'done' },
          { title: `Finding it on the ${NETWORK_WORD[v.network]} network`, sub: `Looking for ${amountText(v)}`, state: 'now' },
          { title: 'Network confirmation', sub: 'Usually a few minutes', state: 'todo' },
          { title: 'Final check by our team', sub: 'Usually within 2 hours', state: 'todo' },
        ]}
      />
      {!v.clientTxHash && (
        <div className="flex flex-col gap-2">
          <Field label="Have the transaction ID? Paste it to speed things up" htmlFor="vw-tx2">
            <input id="vw-tx2" className="field num !text-[14px]" placeholder="Paste it here" value={tx} onChange={(e) => setTx(e.target.value)} />
          </Field>
          <PillButton size="sm" kind="secondary" busy={busy} disabled={!tx.trim()} onClick={() => void paste()}>
            Add transaction ID
          </PillButton>
        </div>
      )}
      {msg && <Notice msg={msg} onClose={() => setMsg(null)} />}
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[14px]">
        <button type="button" className="font-medium text-accent-fg underline-offset-2 hover:underline" onClick={onNotSent}>
          I haven't sent it yet
        </button>
        <PillLink to={`/account/support?topic=funding&ref=${encodeURIComponent('Wallet verification')}`} size="sm" kind="secondary">
          I sent a different amount
        </PillLink>
      </div>
    </>
  );
}

/** V3: found, confirming, and the final check. */
function Found({ v }: { v: WalletVerificationView }) {
  const f = v.found!;
  const confirmed = f.confirmations >= f.requiredConfirmations;
  return (
    <>
      <div className="flex items-center gap-4">
        <Ring part={confirmed ? 0.85 : 0.5 + 0.35 * (f.confirmations / f.requiredConfirmations)} done={confirmed}>
          {confirmed ? (
            <>
              3<small className="block text-[10px] font-medium text-muted">of 4</small>
            </>
          ) : (
            <>
              {f.confirmations}/{f.requiredConfirmations}
              <small className="block text-[10px] font-medium text-muted">confirmed</small>
            </>
          )}
        </Ring>
        <div className="flex flex-col gap-1">
          <b className="text-[17px]">{f.amountMatches ? 'Got it, and it matches' : 'Got it'}</b>
          <span className="text-[13px] text-muted">
            {f.amountMatches ? 'Our team gives it a final check, usually within 2 hours.' : `We received ${amountText(v, f.amount)}, not ${amountText(v)}. Our team will look at it and let you know.`}
          </span>
        </div>
      </div>
      <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 rounded-[14px] bg-track px-4 py-3 text-[14px]">
        <dt className="text-muted">Amount</dt>
        <dd className={cx('num m-0 text-right font-semibold', f.amountMatches ? 'text-up' : 'text-warn')}>
          {amountText(v, f.amount)} {f.amountMatches ? '✓' : ''}
        </dd>
        <dt className="text-muted">Network</dt>
        <dd className="m-0 text-right font-semibold text-up">{CHAIN[v.network]} ✓</dd>
        <dt className="text-muted">To our address</dt>
        <dd className="num m-0 text-right font-semibold text-up">{short(v.receiveAddress)} ✓</dd>
        <dt className="text-muted">Transaction</dt>
        <dd className="num m-0 text-right">
          <a href={f.explorerUrl} target="_blank" rel="noreferrer" className="text-accent-fg underline-offset-2 hover:underline">
            {short(f.txHash)} ↗
          </a>
        </dd>
      </dl>
      <Tracker
        steps={[
          { title: 'You sent it', sub: v.clientSentAt ? when(v.clientSentAt) : '', state: 'done' },
          { title: `Found on the ${NETWORK_WORD[v.network]} network`, sub: when(f.at), state: 'done' },
          { title: confirmed ? 'Confirmed by the network' : 'Network confirmation', sub: `${Math.min(f.confirmations, f.requiredConfirmations)} of ${f.requiredConfirmations}`, state: confirmed ? 'done' : 'now' },
          { title: 'Final check by our team', sub: confirmed ? 'In progress' : 'Next', state: confirmed ? 'now' : 'todo' },
        ]}
      />
      <span className="text-[13px] text-muted">We'll notify you when it's done.</span>
    </>
  );
}

/** V4: verified. */
function Done({ v, onClose }: { v: WalletVerificationView; onClose: () => void }) {
  return (
    <>
      <div className="flex flex-col items-center gap-2 py-2 text-center">
        <span className="grid size-14 place-items-center rounded-full bg-up-bg text-[26px] font-bold text-up">✓</span>
        <b className="text-[18px]">Wallet verified</b>
        <span className="text-[14px] text-muted">Thanks. Your withdrawal is moving again.</span>
      </div>
      {v.creditedAmount && (
        <div className="flex justify-between rounded-[14px] bg-up-bg px-4 py-3 font-semibold text-up">
          <span>Back in your balance</span>
          <span className="num">+{amountText(v, v.creditedAmount)}</span>
        </div>
      )}
      <span className="text-[13px] text-muted">You won't need to verify {v.addressLabel ?? 'this wallet'} again for future withdrawals.</span>
      <PillButton onClick={onClose}>Back to withdrawal</PillButton>
    </>
  );
}

/** V5: the amount was different, or time ran out. */
function Problem({ v, onUpdate }: { v: WalletVerificationView; onUpdate: (v: WalletVerificationView) => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [asked, setAsked] = useState(false);
  const again = async () => {
    setBusy(true);
    setMsg(null);
    try {
      onUpdate(await api.post<WalletVerificationView>(`/api/withdrawals/addresses/${v.addressId}/verification/new-details`));
      setAsked(true);
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };
  const expired = v.status === 'expired';
  return (
    <>
      <div className={cx('flex flex-col gap-2 rounded-[14px] px-4 py-3 text-[14px]', expired ? 'bg-warn-bg' : 'bg-down-bg')}>
        <b className={expired ? 'text-warn' : 'text-down'}>{expired ? 'Time ran out' : "We couldn't confirm this wallet"}</b>
        <span>{expired ? `We didn't receive the transfer by ${when(v.expiresAt)}. Start again and we'll give you a new amount.` : v.rejectReason}</span>
        {v.creditedAmount && !expired && <span className="text-muted">{amountText(v, v.creditedAmount)} is back in your balance.</span>}
      </div>
      {msg && <Notice msg={msg} onClose={() => setMsg(null)} />}
      {asked ? (
        <div className="rounded-[14px] bg-track px-4 py-3 text-[14px]">We've asked our team for a new amount. We'll notify you when it's ready.</div>
      ) : (
        <PillButton busy={busy} onClick={() => void again()}>
          {expired ? 'Start again' : 'Ask for a new amount'}
        </PillButton>
      )}
    </>
  );
}
